import React, { useEffect, useRef, useState } from 'react';
import "./NowaForm.css";
import { Button } from 'devextreme-react/button';
import { NumberBox } from 'devextreme-react/number-box';
import { SelectBox } from 'devextreme-react/select-box';
import { DayCountConvention, RateConvention, IQueryParameters } from './Models';
import { Validator } from 'devextreme-react/validator';
import { ValidationGroup, ValidationGroupRef } from 'devextreme-react/validation-group';
import { RequiredRule } from 'devextreme-react/data-grid';
import { translate as t, getLocale, getRateFloorDropdown, getBankingDayConventionDropdown, getRateConventionDropdown, ITranslations } from './i18n';
import Collapsible from "./Collapsible";
import CustomDateBox from "./CustomDateBox";

interface IProps {
  onSubmit: (input: IQueryParameters) => void;
  holidays: Array<Date>;
  className?: string;
}

const locale = getLocale();

const NowaForm: React.FC<IProps> = ({ onSubmit, holidays, className }) => {

  const today = new Date();
  const defaultLag = 2;
  const startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const endDate = today;
  const defaultNotional = 1_000_000;
  const defaultSpread = 0;
  const defaultStrike = 0;

  const rateFloors = getRateFloorDropdown(locale);
  const defaultRateFloor = rateFloors[0];

  const dcvs: Array<DayCountConvention> = ["ACT365", "ACT360"];
  const defaultDayCountConvention = dcvs[0];

  const roundings = [2, 3, 4, 5, 6];
  const defaultRounding = 5;

  const bdcs = getBankingDayConventionDropdown(locale);
  const defaultBankingDayConvention = bdcs[0];

  const rateConventions = getRateConventionDropdown(locale);
  const defaultRateConvention = rateConventions[0];

  const [fromDate, setFromDate] = useState(startDate);
  const [toDate, setToDate] = useState(endDate);
  const [lag, setLag] = useState(defaultLag);
  const [notional, setNotional] = useState(defaultNotional);
  const [spread, setSpread] = useState(defaultSpread);
  const [rounding, setRounding] = useState(defaultRounding);
  const [dayCountConvention, setDayCountConvention] = useState(defaultDayCountConvention);
  const [bankingDayConvention, setBankingDayConvention] = useState(defaultBankingDayConvention.value);
  const [rateFloor, setRateFloor] = useState(defaultRateFloor.value);
  const [strike, setStrike] = useState(defaultStrike);
  const [rateConvention, setRateConvention] = useState(defaultRateConvention.value);

  const validationGroupRef = useRef<ValidationGroupRef>(null);

  function reset() {
    setFromDate(startDate);
    setToDate(endDate);
    setLag(defaultLag);
    setNotional(defaultNotional);
    setSpread(defaultSpread);
    setRounding(defaultRounding);
    setDayCountConvention(defaultDayCountConvention);
    setBankingDayConvention(defaultBankingDayConvention.value);
    setRateFloor(defaultRateFloor.value);
    setRateConvention(defaultRateConvention.value);
  }

  useEffect(() => {
    const validForm = validationGroupRef?.current?.instance()?.validate().isValid;

    if (!validForm) {
      return;
    }

    onSubmit({ fromDate, toDate, lag, notional, spread, rounding, dayCountConvention, bankingDayConvention: bankingDayConvention, rateConvention, rateFloor, strike });
  }, [lag, notional, spread, rounding, dayCountConvention, bankingDayConvention, rateConvention, fromDate, toDate, rateFloor, strike, onSubmit]);

  const lagLabelDictionary: { [key in RateConvention]: keyof ITranslations } = {
    "DelayedPayment": "nowaForm.lagLabelDelayedPayment",
    "Lockout": "nowaForm.lagLabelLockout",
    "ObservationShift": "nowaForm.lagLabelObservationShift",
    "Lookback": "nowaForm.lagLabelLookback",
  };

  const lagLabel = lagLabelDictionary[rateConvention]

  return <div className={className}>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <h2>{t("nowaForm.title")}</h2>
      <Button stylingMode='text' text={t("nowaForm.reset")} icon="revert" onClick={() => reset()} />
    </div>

    <form>
      <ValidationGroup ref={validationGroupRef}>

        <div className='nowa-form'>

          <CustomDateBox hint={t("nowaForm.startDate.hint")} name='from' className='editor' stylingMode="filled" min={new Date(2020, 0, 2)} max={today} label={t("nowaForm.startDate")} value={fromDate} onValueChanged={setFromDate} holidays={holidays} />

          <CustomDateBox hint={t("nowaForm.endDate.hint")} name='to' className='editor' stylingMode="filled" min={new Date(2020, 0, 3)} label={t("nowaForm.endDate")} value={toDate} onValueChanged={setToDate} holidays={holidays} />

          <NumberBox name='lag' className='editor' stylingMode="filled" label={t(lagLabel)} min={0} value={lag} onValueChanged={(e) => setLag(e.value)}>
            <Validator>
              <RequiredRule />
            </Validator>
          </NumberBox>

          <NumberBox hint={t("nowaForm.principal.hint")} name='principal' className='editor' stylingMode="filled" label={t("nowaForm.principal")} min={0} max={1_000_000_000_000_00} step={1_000_000} format={{ type: "fixedPoint", precision: 0 }} value={notional} onValueChanged={(e) => setNotional(e.value)}>
            <Validator>
              <RequiredRule />
            </Validator>
          </NumberBox>

          <SelectBox hint={t("nowaForm.rateConvention.hint")} name='rateConvention' className='editor' stylingMode="filled" label={t("nowaForm.rateConvention")} dataSource={rateConventions} valueExpr={"value"} displayExpr={"name"} value={rateConvention} onValueChanged={(e) => setRateConvention(e.value)} />

          <NumberBox hint={t("nowaForm.spread.hint")} name='spread' className='editor' stylingMode="filled" label={t("nowaForm.spread")} step={0.01} format={"#0.00"} min={-100.00} max={100.00} value={spread} onValueChanged={(e) => setSpread(e.value)}>
            <Validator>
              <RequiredRule />
            </Validator>
          </NumberBox>
        </div>

        <Collapsible title={t("nowaForm.advancedOptions")} style={{ margin: "0.75rem 0 0.25rem 0" }}>
          <div className='nowa-form'>
            <SelectBox hint={t("nowaForm.dayCountConvention.hint")} name='dayCountConvention' className='editor' stylingMode="filled" label={t("nowaForm.dayCountConvention")} dataSource={dcvs} value={dayCountConvention} onValueChanged={(e) => setDayCountConvention(e.value)} />

            <SelectBox hint={t("nowaForm.bankingDayConvention.hint")} name='bankingDayConvention' className='editor' stylingMode="filled" label={t("nowaForm.bankingDayConvention")} dataSource={bdcs} valueExpr={"value"} displayExpr={"name"} value={bankingDayConvention} onValueChanged={(e) => setBankingDayConvention(e.value)} />

            <SelectBox hint={t("nowaForm.rateFloor.hint")} name='rateFloor' className='editor' stylingMode="filled" label={t("nowaForm.rateFloor")} dataSource={rateFloors} valueExpr={"value"} displayExpr={"name"} value={rateFloor} onValueChanged={(e) => setRateFloor(e.value)} />
            {rateFloor !== defaultRateFloor.value ? <NumberBox hint={t("nowaForm.strike.hint")} name='strike' className='editor' stylingMode="filled" label={t("nowaForm.strike")} format={"#0.00"} step={0.01} min={-100.00} max={100.00} value={strike} onValueChanged={(e) => setStrike(e.value)} /> : null}

            <SelectBox hint={t("nowaForm.rounding.hint")} name='rounding' className='editor' stylingMode="filled" label={t("nowaForm.rounding")} value={rounding} dataSource={roundings} onValueChanged={(e) => setRounding(e.value)} />
          </div>
        </Collapsible>
      </ValidationGroup>
    </form>
  </div>;
}

export default NowaForm;